import {
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  DecimalScale,
  Form,
  FormDialogMode,
  FormOptions,
  Section,
  SelectOption,
  useNotify,
  UserPermission
} from '@globaleris/ui.library'
import { Box, Button, Card, Grid, makeStyles, Typography, useTheme } from '@material-ui/core'
import { Add, AddCircleOutline, Delete, Edit, PlusOne } from '@material-ui/icons'
import moment, { duration } from 'moment'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import { EnumEstadoViajeOpLogistica } from '../../../api/oData/enums/enumEstadoViajeOpLogistica'
import { EnumTipoCosteCisternas } from '../../../api/oData/enums/enumTipoCosteCisternas'
import Tanks, { Carrier } from '../../../api/Pioneer/model/Tanks'
import { ErrorDetailList } from '../../../components/errorDetailList/errorDetailList'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumEnumToValueNumber, enumValueToKey } from '../../../helpers'
import { UserRoles } from '../../../model/enums/userRoles'
import { hasPermission, hasRole } from '../../../helpers/permissionsHelper'
import {
  FormDialogDefinitionProps,
  useConfirmationDialog,
  useErrorDetailListDialog,
  useFormDialog
} from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { InformationDialogs } from '../../../model/enums/informationDialogs'
import { Resources } from '../../../model/RBAC/constants'
import { getCustomerDomain, notifyError } from '../../../redux'
import { checkDestinations, checkFormData, isDatePast, isStatePending } from '../tanks.helpers'
import { mapperOnSaveCarriersTable, mapperOnSaveDestinationsTable } from '../tanks.mapper'
import {
  CarrierTableModel,
  DataFormModel,
  DefaultCosts,
  DestinationTableModel,
  DestinyModel,
  TankModel
} from '../tanks.model'
import {
  addTanks,
  fetchCarriersAdd,
  fetchClients,
  fetchCostTypes,
  fetchDefaultCosts,
  fetchDestinations,
  fetchGetTanksSize,
  fetchOriginPlants,
  fetchTankTravel,
  updateTank
} from '../tanks.service'
import {
  carriersFormDependenciesProps as carriersFormDepProps,
  Dep,
  destinationsFormDependenciesProps as destinationsFormDepProps,
  IsFetchingDeps,
  SelectOptions
} from '../tanks.types'
import { LoadingForm } from './loadingForm'
import { styles } from './tankForm.styles'
import { throttle } from '../../../helpers/throttle'
import {
  DEFAULT_FULL_TANK_QUANTITY,
  DEFAULT_NOT_FULL_TANK_QUANTITY,
  DEFAULT_PCS
} from '../tanks.constants'

const initialDependencies = {
  optionsQuantity: [],
  optionsQuantityCopy: [],
  clients: [],
  destinations: [],
  carriers: [],
  costTypes: [],
  originPlantElements: []
}

const initialCarrierFormState: carriersFormDepProps = {
  formState: {},
  carriers: [],
  costTypes: []
}

const initialDestinationFormState: destinationsFormDepProps = {
  formState: {},
  destinations: []
}

export interface TankFormProps {
  tank: TankModel | null
  readOnly?: boolean
}

export const TankForm: React.FC<TankFormProps> = ({ tank, readOnly = false }) => {
  let history = useHistory()
  const [t] = useTranslation(['globalerisLibrary', 'tanks', 'enums'])
  const currentTheme = useTheme()
  const useStyles = makeStyles(styles(currentTheme))
  const classes = useStyles()
  const { info } = useNotify()
  const { getConfirmation, closeConfirmation, getInformation, closeInformation } =
    useConfirmationDialog()
  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog } = useFormDialog()
  const [tankData, setTankData] = useState<any>(tank)
  const [destinationsSelected, setDestinationsSelected] = useState<DestinationTableModel[]>([])
  const [carriersSelected, setCarriersSelected] = useState<CarrierTableModel[]>([])
  const [dependencies, setDependencies] = useState<SelectOptions>(initialDependencies)
  const [dataForm, setDataForm] = useState<DataFormModel>(
    hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) || hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
      ? {
          orderDate: moment().toDate(),
          chargeDate: hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ? undefined : moment().toDate(),
          client: undefined,
          originPlant: undefined,
          deliveryNote: undefined,
          destination: undefined,
          kg: parseFloat((DEFAULT_FULL_TANK_QUANTITY / DEFAULT_PCS).toFixed(2)),
          pcs: DEFAULT_PCS,
          orderQuantity: DEFAULT_FULL_TANK_QUANTITY,
          orderKg: parseFloat((DEFAULT_FULL_TANK_QUANTITY / DEFAULT_PCS).toFixed(2)),
          isFullTank: true,
          quantity: undefined
        }
      : {
          orderDate: moment().toDate(),
          chargeDate: moment().toDate(),
          client: undefined,
          quantity: undefined,
          pcs: DEFAULT_PCS,
          kg: undefined,
          orderQuantity: undefined,
          orderKg: undefined,
          originPlant: undefined,
          deliveryNote: undefined,
          destination: undefined,
          isFullTank: undefined
        }
  )
  const [filteredDestinationsOptions, setFilteredDestinationsOptions] = useState<SelectOption[]>([])
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [isFetchingTank, setIsFetchingTank] = useState<boolean>(false)
  const [isFetchingOriginPlants, setIsFetchingOriginPlants] = useState<boolean>(false)
  const [isFetchingDestinations, setIsFetchingDestinations] = useState<boolean>(false)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [switchFullTank, setSwitchFullTank] = useState<boolean>(true)
  const [defaultCosts, setDefaultCosts] = useState<DefaultCosts[]>([])

  // Dependencies used for Carriers (Transportistas) options
  const [carriersFormDeps, setCarriersFormDeps] =
    useState<carriersFormDepProps>(initialCarrierFormState)
  const [carriersDefaultCostsFormDeps, setCarriersDefaultCostsFormDeps] = useState<SelectOption[]>(
    []
  )

  // Dependencies used for Destinations options
  const [destinationsFormDeps, setDestinationsFormDeps] = useState<destinationsFormDepProps>(
    initialDestinationFormState
  )

  // Loading state to be used in Selects
  const [isFetchingDeps, setIsFetchingDeps] = useState<IsFetchingDeps>({
    carriers: false,
    clients: false,
    destinations: false,
    originPlants: false,
    kg: false
  })

  const [selectedClient, setSelectedClient] = useState<number | undefined>(undefined)
  const [invalidDate, setInvalidDate] = useState<boolean>(false)
  const [disableOriginPlant, setDisableOriginPlant] = useState<boolean>(false)
  const [disableSave, setDisableSave] = useState<boolean>(false)

  // Function used to switch ON/OFF fetching state for certain dependency
  const switchFetchingDeps = (dep: Dep): void => {
    setIsFetchingDeps(deps => ({ ...deps, [dep]: !deps[dep] }))
  }

  const getOriginPlants = async (destinations?: number[], clientId?: number) => {
    switchFetchingDeps('originPlants')
    setIsFetchingOriginPlants(true)
    await fetchOriginPlants(destinations, clientId)
      .then(response => {
        const plantsList = response?.data ?? []
        setDependencies(prev => ({
          ...prev,
          originPlantElements: plantsList
        }))

        if (plantsList.length === 1) {
          setDataForm(prevData => ({ ...prevData, originPlant: plantsList[0] }))
          setDisableOriginPlant(true)
        } else {
          setDisableOriginPlant(false)
        }

        setIsFetchingDeps(deps => ({ ...deps, originPlants: false }))
      })
      .finally(() => {
        setIsFetchingOriginPlants(false)
      })
  }

  const getDestinations = async (
    destinationsIds?: number[],
    clientId?: number,
    originPlantId?: number
  ) => {
    switchFetchingDeps('destinations')
    setIsFetchingDestinations(true)
    return fetchDestinations(destinationsIds, clientId, originPlantId)
      .then(response => {
        if (response?.data) {
          const destinationsList = response?.data ?? []
          getDestinationsOptions(destinationsList)
          setDependencies(prev => ({
            ...prev,
            destinations: destinationsList
          }))

          const initDestination: Partial<DestinationTableModel> =
            destinationsList.length === 1
              ? {
                  destinationId: +destinationsList[0].value,
                  description: destinationsList[0].label
                }
              : {}

          setDestinationsFormDeps(prevDependencies => ({
            ...prevDependencies,
            formState: initDestination
          }))
        }
        switchFetchingDeps('destinations')
      })
      .finally(() => {
        setIsFetchingDestinations(false)
      })
  }

  const getDefaultCosts = async (tankDate?: string) => {
    await fetchDefaultCosts(tankDate).then(response => {
      if (!response.success) {
        return
      }
      const defaultCosts = response.data && response.data.length > 0 ? response.data : []
      setDefaultCosts(defaultCosts)
    })
  }

  const getTankTravel = async (id: number, quantityOps?: SelectOption[]) => {
    setIsFetchingTank(true)
    await fetchTankTravel(id)
      .then(response => {
        if (!response.success) {
          return
        }
        const tank = response.data && response.data.length > 0 ? response.data[0] : null
        if (tank) {
          setTankData(tank)
          mapTank(tank, quantityOps)
        }
      })
      .finally(() => {
        setIsFetchingTank(false)
      })
  }

  const getDestinationsOptions = (destinationsList: SelectOption[]) => {
    const options =
      destinationsList.filter(
        option => !destinationsSelected.map(d => d.destinationId)?.includes(+option.value)
      ) ?? []
    setFilteredDestinationsOptions(options)
  }

  const mapTank = (tankInfo: Tanks, quantityOps?: SelectOption[]): void => {
    let quantityOptions: SelectOption[] =
      dependencies.optionsQuantity && dependencies.optionsQuantity.length > 0
        ? dependencies.optionsQuantity
        : (quantityOps ?? [])

    let tankQuantity = quantityOptions.find(x => x.label === `${tankInfo.quantity}`)

    if (tankInfo.quantity && !tankQuantity) {
      quantityOptions = [
        ...quantityOptions,
        {
          value: tankInfo.quantity,
          label: `${tankInfo.quantity}`
        }
      ]
      setDependencies({
        ...dependencies,
        optionsQuantity: quantityOptions
      })
    }

    setDataForm({
      orderDate:
        tankInfo.orderDate && tankInfo.orderDate != null
          ? moment(tankInfo.orderDate).toDate()
          : undefined,
      chargeDate:
        tankInfo.tripDate && tankInfo.tripDate != null
          ? moment(tankInfo.tripDate).toDate()
          : undefined,
      dischargeDate: tankInfo.dischargeDate ? moment(tankInfo.dischargeDate).toDate() : undefined,
      client: { value: tankInfo.client?.id!, label: tankInfo.client?.description! },
      originPlant: { value: tankInfo.originPlant?.id!, label: tankInfo.originPlant?.description! },
      deliveryNote: tankInfo.deliveryNote == null ? '' : `${tankInfo.deliveryNote}`,
      quantity: quantityOptions.find(x => x.label === `${tankInfo.quantity}`)?.value,
      quantityOption: quantityOptions.find(x => x.label === `${tankInfo.quantity}`),
      kg: tankInfo.kg,
      pcs: tankInfo.pcs ?? 0,
      tsoCode: `${tankInfo.tsoCode}`,
      orderQuantity: tankInfo.orderQuantity,
      orderKg: tankInfo.orderKg,
      comments: tankInfo.comments == null ? '' : `${tankInfo.comments}`,
      isFullTank: tankInfo.quantity != DEFAULT_NOT_FULL_TANK_QUANTITY
    })

    if (tankInfo.destinations !== undefined) {
      const destinations: DestinationTableModel[] = tankInfo.destinations.map(
        (dest: DestinyModel) => {
          return {
            id: dest.id,
            destinationId: dest.destinyPlant.id,
            description: dest.destinyPlant.description,
            percentage: dest.percentage,
            canDelete: !readOnly
          } as DestinationTableModel
        }
      )
      setDestinationsSelected(destinations)
    }

    if (tankInfo.carriers !== undefined) {
      const carriers: CarrierTableModel[] = tankInfo.carriers.map((carrier: Carrier) => {
        return {
          carrierTripId: carrier.carrierTripId,
          carrierId: carrier.carrierId,
          description: carrier.carrierDescription,
          quantity: carrier.amount,
          costType: enumEnumToValueNumber(EnumTipoCosteCisternas, carrier.costType),
          lgmaster: carrier.lgMaster,
          canDelete: !readOnly,
          reactId: carrier.carrierTripId
        } as CarrierTableModel
      })
      setCarriersSelected(carriers)
    }
    setSelectedClient(tank?.client?.id)
    // setIsFetching(false)
  }

  const fetchDependencies = async () => {
    switchFetchingDeps('carriers')
    switchFetchingDeps('clients')
    setIsFetching(true)
    await Promise.all([
      await fetchGetTanksSize(),
      await fetchCarriersAdd(),
      await fetchClients(),
      await fetchCostTypes()
    ])
      .then(response => {
        const quantityOptions = response[0]
        const carriers = response[1]
        const clients = response[2]
        const costTypes = response[3]

        setDependencies(prev => ({
          ...prev,
          optionsQuantity: quantityOptions,
          optionsQuantityCopy: quantityOptions,
          carriers,
          clients,
          costTypes
        }))
        switchFetchingDeps('carriers')
        switchFetchingDeps('clients')

        if (tank) {
          // If tank is defined (edit)
          getTankTravel(tank.id!, quantityOptions)
          getOriginPlants(tank ? tank.destinations : [], tank ? tank.client?.id : undefined)
          getDefaultCosts(moment(tank.tripDate).format('YYYY-MM-DD'))

          if (
            readOnly ||
            (tankData.tripDate != null && isDatePast(moment(tankData.tripDate).toDate()))
          ) {
            // if Date is past Gas Day => Disable Save Button
            setDisableSave(true)
          } else {
            setDisableSave(false)
          }

          if (
            hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
            hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
          ) {
            setSwitchFullTank(tank.quantity === DEFAULT_FULL_TANK_QUANTITY)
          }
        } else {
          getDefaultCosts(moment().format('YYYY-MM-DD'))
          if (
            hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
            hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
          ) {
            setDataForm(data => {
              let newFormData: DataFormModel = {
                ...data,
                quantity: quantityOptions.find(
                  c => c.label === DEFAULT_FULL_TANK_QUANTITY.toString()
                )?.value,
                quantityOption: quantityOptions.find(
                  c => c.label === DEFAULT_FULL_TANK_QUANTITY.toString()
                )
              }
              if (clients && clients.length === 1) {
                newFormData = {
                  ...newFormData,
                  client: { value: clients[0].value, label: clients[0].label }
                }
                handleClientChange(Number(clients[0].value))
              }

              return newFormData
            })
          } else {
            if (clients && clients.length === 1) {
              setDataForm(data => ({
                ...data,
                client: { value: clients[0].value, label: clients[0].label }
              }))
              handleClientChange(Number(clients[0].value))
            }
          }
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }

  const mainFormDefinition: Section[] = React.useMemo<Section[]>(() => {
    return [
      {
        title: t('tanks:forms.add.titleTank'),
        inputs: [
          {
            name: 'orderDate',
            label: t('tanks:forms.add.inputs.dateOrder'),
            type: DataType.DATE,
            required:
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            readOnly: true,
            display: hasRole(UserRoles.TANKS_REPSOL_TPA)
          },
          {
            name: 'chargeDate',
            label: hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
              ? t('tanks:forms.add.inputs.dateCharge')
              : t('tanks:forms.add.inputs.date'),
            type: DataType.DATE,
            required: !hasRole(UserRoles.TANKS_REPSOL_GESTIONADO),
            display: !hasRole(UserRoles.TANKS_REPSOL_GESTIONADO),
            minDate: moment().toISOString(),
            readOnly:
              readOnly ||
              ((tankData
                ? !isStatePending(
                    enumEnumToValueNumber(EnumEstadoViajeOpLogistica, tankData.tripState)
                  )
                : false) &&
                //Si estamos en edición y tiene rol no gestionado se puede editar si está en estado registrado.
                ((hasRole(UserRoles.TANKS_REPSOL_TPA) &&
                  tankData.tripDate &&
                  isDatePast(moment(tankData.tripDate).toDate())) ||
                (hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO) &&
                  enumEnumToValueNumber(EnumEstadoViajeOpLogistica, tankData.tripState) !=
                    EnumEstadoViajeOpLogistica.Registrado)
                  ? true
                  : false)),

            errorState: invalidDate,
            errorText: 'No es posible crear Cisternas a pasado del Día del Gas',

            onChange: value => {
              getDefaultCosts(moment(value).format('YYYY-MM-DD'))
              if (!value) {
                return
              }
              if (value && isDatePast(moment(value).toDate())) {
                // if Date is past Gas Day => Show error and Disable Save Button
                setInvalidDate(true)
                setDisableSave(true)
              } else {
                setDisableSave(false)
                setInvalidDate(false)
              }
            }
          },
          {
            name: 'dischargeDate',
            label: t('tanks:forms.add.inputs.dischargeDate'),
            type: DataType.DATE,
            required: hasRole(UserRoles.TANKS_REPSOL_GESTIONADO), //Sólo es obligatorio si es gestionado
            minDate: moment().toISOString(),
            readOnly: readOnly,
            display: !hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO) //Solo se oculta para no gestionados
          },
          {
            name: 'client.value',
            label: t('tanks:forms.add.inputs.client'),
            type: DataType.SELECT,
            selectOptions: dependencies.clients,
            required: true,
            readOnly: readOnly || (dependencies.clients && dependencies.clients.length === 1),
            onChange: clientID => handleClientChange(+clientID),
            isLoading: isFetchingDeps.clients || isFetching,
            loadingText: 'Cargando Clientes disponibles...'
          },
          {
            name: 'isFullTank',
            label: t('tanks:forms.add.inputs.isFullTank'),
            type: DataType.SWITCH,
            required:
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            display:
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            onChange: value => handleTankSwitchChange(Boolean(value)),
            readOnly: readOnly
          },
          {
            name: 'quantity',
            label: t('tanks:forms.add.inputs.quantity'),
            type: DataType.SELECT,
            selectOptions: dependencies.optionsQuantity,
            required: true,
            readOnly: readOnly,
            defaultValue: dependencies.optionsQuantity.find(
              c => c.label === DEFAULT_FULL_TANK_QUANTITY.toString()
            ),
            display:
              !hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) &&
              !hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            onChange: async (value, formState: any) => {
              switchFetchingDeps('kg')
              const quantity = dependencies.optionsQuantity.find(x => x.value === value)
              const clientUser =
                hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) &&
                hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
              if (quantity !== undefined) {
                setDataForm({
                  ...formState,
                  quantity: quantity.value,
                  quantityOption: quantity,
                  kg: calculateKgQuantity(formState.pcs, parseInt(quantity!.label)),
                  orderKg: formState.orderKg,
                  orderQuantity: clientUser
                    ? calculateKwhQuantity(dataForm.pcs, formState.orderKg)
                    : formState.orderQuantity
                })
              }
              switchFetchingDeps('kg')
            }
          },
          {
            name: 'pcs',
            label: t('tanks:forms.add.inputs.pcs'),
            type: DataType.NUMBER,
            decimalScale: DecimalScale.DEFAULT,
            required: true,
            readOnly: readOnly,
            display:
              !hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) &&
              !hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            onChange: (value, formState: any) => {
              const quantity = dependencies.optionsQuantity.find(
                x => x.value === formState.quantity.value
              )
              var pcs = parseInt(value.toString())
              if (quantity !== undefined) {
                setDataForm({
                  ...dataForm!,
                  quantity: parseInt(quantity!.label),
                  quantityOption: quantity,
                  kg: calculateKgQuantity(pcs, parseInt(quantity!.label)),
                  orderQuantity: calculateKwhQuantity(pcs, formState.orderKg)
                })
              }
            }
          },
          {
            name: 'kg',
            label: t('tanks:forms.add.inputs.kg'),
            type: DataType.NUMBER,
            required: true,
            display: !(
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
            ),
            readOnly:
              readOnly ||
              ((hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
                hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)) &&
                switchFullTank)
          },
          {
            name: 'orderQuantity',
            label: t('tanks:forms.add.inputs.orderQuantity'),
            type: DataType.NUMBER,
            decimalScale: DecimalScale.ENERGY,
            readOnly:
              readOnly ||
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            required:
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_TPA),
            display:
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_TPA)
          },
          {
            name: 'orderKg',
            label: t('tanks:forms.add.inputs.orderKg'),
            //decimalScale: DecimalScale.ENERGY,
            type: DataType.NUMBER,
            required:
              (hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
                hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)) &&
              !switchFullTank,
            display:
              ((hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
                hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)) &&
                !switchFullTank) ||
              hasRole(UserRoles.TANKS_REPSOL_TPA),
            readOnly: readOnly,
            onChange: async (kgs, formState: any) => {
              const quantity = dependencies.optionsQuantity.find(
                x => x.value === formState.quantity
              )

              if (kgs !== undefined && quantity !== undefined) {
                setDataForm({
                  ...dataForm,
                  orderKg: +kgs,
                  orderQuantity: calculateKwhQuantity(dataForm.pcs, +kgs),
                  quantity: quantity.value,
                  quantityOption: quantity,
                  kg: calculateKgQuantity(formState.pcs, parseInt(quantity!.label))
                })
              }
            }
          },
          {
            name: 'originPlant.value',
            label: t('tanks:forms.add.inputs.originPlant'),
            type: DataType.SELECT,
            selectOptions: dependencies.originPlantElements,
            helpText:
              !readOnly && !dataForm.client
                ? t('tanks:forms.add.helpTexts.originPlant.selectPlant')
                : destinationsSelected.length > 0 && !readOnly
                  ? t('tanks:forms.add.helpTexts.originPlant.showMore')
                  : '',
            isLoading: isFetchingDeps.originPlants || isFetching,
            loadingText: 'Cargando Plantas de Origen disponibles...',
            readOnly:
              readOnly ||
              disableOriginPlant ||
              !dataForm.client ||
              isFetchingDeps.originPlants ||
              isFetchingDeps.destinations ||
              isFetching,
            required: !hasRole(UserRoles.TANKS_REPSOL_GESTIONADO),
            display: !hasRole(UserRoles.TANKS_REPSOL_GESTIONADO),
            onChange: originPlantID => handleOriginPlantsChange(+originPlantID)
          },
          {
            name: 'deliveryNote',
            label: t('tanks:forms.add.inputs.deliveryNote'),
            type: DataType.TEXT,
            readOnly:
              readOnly ||
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO),
            required: false,
            onChange: value =>
              setDataForm(data => ({
                ...data,
                deliveryNote: value == null ? '' : `${value}`
              }))
          },
          {
            name: 'comments',
            label: t('tanks:forms.add.inputs.comments'),
            type: DataType.TEXT,
            required: false,
            display:
              hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_TPA),
            inputSize: 12,
            readOnly: readOnly
          },
          {
            display: false,
            name: 'costTypes.value',
            type: DataType.SELECT,
            selectOptions: dependencies.costTypes,
            required: false
          }
        ]
      }
    ]
  }, [
    readOnly,
    invalidDate,
    dependencies.originPlantElements,
    dependencies.costTypes,
    dependencies.optionsQuantity,
    dependencies.clients,
    isFetchingDeps.clients,
    isFetchingDeps.originPlants,
    isFetchingDeps.destinations,
    isFetching,
    switchFullTank
  ])

  const checkDateHasChanged = (formData: DataFormModel): boolean => {
    //Evaluamos si la fecha ha cambiado cuando hay fecha y el estado no es registrado o validado
    if (
      tankData?.tripDate &&
      formData?.chargeDate &&
      tankData?.tripState &&
      enumValueToKey(EnumEstadoViajeOpLogistica, tankData.tripState) !=
        EnumEstadoViajeOpLogistica.Registrado &&
      enumValueToKey(EnumEstadoViajeOpLogistica, tankData.tripState) !=
        EnumEstadoViajeOpLogistica.Validado &&
      !moment(tankData?.tripDate).isSame(formData?.chargeDate, 'day')
    ) {
      return true
    }
    return false
  }

  const checkOnlyDischargeDateHasChanged = (formData: DataFormModel): boolean => {
    const checkDateIsSame = (tankDate?: Date, formDate?: Date): boolean => {
      if (!tankDate && !formDate) {
        return true
      }

      if (!tankDate || !formDate) {
        return false
      }

      return moment(tankDate).isSame(formDate, 'day')
    }

    const checkSelectOptionIsSame = (
      tankValue?: string | number,
      formValue?: SelectOption
    ): boolean => `${tankValue ?? ''}` === `${formValue?.value ?? ''}`

    const getFormQuantity = (formData: DataFormModel): string => {
      const quantityOptionLabel =
        typeof formData.quantityOption !== 'string'
          ? formData.quantityOption?.label
          : formData.quantityOption
      const quantityLabel =
        quantityOptionLabel ??
        dependencies.optionsQuantity.find(option => `${option.value}` === `${formData.quantity}`)
          ?.label

      return `${quantityLabel ?? formData.quantity ?? ''}`
    }

    const dischargeDateHasChanged = !checkDateIsSame(
      tankData?.dischargeDate,
      formData?.dischargeDate
    )

    const chargeDateHasChanged = !checkDateIsSame(tankData?.tripDate, formData?.chargeDate)
    const clientHasChanged = !checkSelectOptionIsSame(tankData?.client?.id, formData?.client)
    const originPlantHasChanged = !checkSelectOptionIsSame(
      tankData?.originPlant?.id,
      formData?.originPlant
    )
    const deliveryNoteHasChanged =
      `${tankData?.deliveryNote ?? ''}` !== `${formData?.deliveryNote ?? ''}`
    const kgHasChanged = tankData?.kg !== formData?.kg
    const pcsHasChanged = tankData?.pcs !== formData?.pcs

    if (
      dischargeDateHasChanged &&
      !chargeDateHasChanged &&
      !clientHasChanged &&
      !kgHasChanged &&
      !pcsHasChanged &&
      !originPlantHasChanged &&
      !deliveryNoteHasChanged
    ) {
      return true
    }

    return false
  }

  const showInfoMessage = async () => {
    await getInformation(InformationDialogs.TANKS_DATE_CHANGED_MESSAGE).then(res => {
      closeInformation()
    })
  }

  const createTankShipment = (formData: DataFormModel | undefined, validated: boolean): any => {
    if (!formData) {
      history.push(NonAuthRoutes.tanks)
      return
    }

    if (!checkFormData(formData, mainFormDefinition, carriersSelected, destinationsSelected)) {
      return
    }

    setIsSaving(true)
    if (formData && destinationsSelected && carriersSelected) {
      if (!checkDestinations(destinationsSelected)) {
        notifyError(t('tanks:notifications.error.destinationsPercent'))
        setIsSaving(false)
        return
      }
      var newState =
        hasRole(UserRoles.TANKS_REPSOL_TPA) && validated
          ? EnumEstadoViajeOpLogistica.Validado
          : undefined
      addTanks(formData, destinationsSelected, carriersSelected, newState).then(async response => {
        if (response.success) {
          info(t('tanks:forms.add.saveOk'))
          history.push(NonAuthRoutes.tanks)
        } else {
          response.error
            ? showErrorDetailListDialog(t('tanks:forms.add.error.createTank'), [response.error])
            : notifyError(t('tanks:forms.edit.error.updateTank'))
        }
        setIsSaving(false)
      })
    }
  }

  const editTankShipment = async (formData: any | undefined, validate: boolean) => {
    if (!formData) {
      history.push(NonAuthRoutes.tanks)
      return
    }

    if (!checkFormData(dataForm, mainFormDefinition, carriersSelected, destinationsSelected)) {
      return
    }

    if (dataForm && destinationsSelected && carriersSelected) {
      if (checkDateHasChanged(formData)) {
        await showInfoMessage()
      }

      if (!checkDestinations(destinationsSelected)) {
        notifyError(t('tanks:notifications.error.destinationsPercent'))
        return
      }

      let newState = checkOnlyDischargeDateHasChanged(formData)
        ? enumValueToKey(EnumEstadoViajeOpLogistica, tankData.tripState)
        : hasRole(UserRoles.TANKS_REPSOL_TPA)
          ? !validate &&
            enumValueToKey(EnumEstadoViajeOpLogistica, tankData.tripState) ==
              EnumEstadoViajeOpLogistica.Registrado //No es necesario evaluar los estados de anulación ya que en esos estados no se permite editar
            ? EnumEstadoViajeOpLogistica.Registrado
            : EnumEstadoViajeOpLogistica.Validado
          : hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
              hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO) //No es necesario evaluar los estados de anulación ya que en esos estados no se permite editar
            ? EnumEstadoViajeOpLogistica.Registrado
            : enumValueToKey(EnumEstadoViajeOpLogistica, tankData.tripState)

      updateTank(tankData, dataForm, destinationsSelected, carriersSelected, newState).then(
        async response => {
          if (response.success) {
            info(t('tanks:forms.add.saveOk'))
            history.push(NonAuthRoutes.tanks)
          } else {
            response.error
              ? showErrorDetailListDialog(t('tanks:forms.add.error.updateTank'), [response.error])
              : notifyError(t('tanks:forms.edit.error.updateTank'))
          }
        }
      )
    }
  }

  //#region dataTableOptions
  const options: FormOptions = React.useMemo(() => {
    return {
      elevation: 0,
      columns: 3,
      analytics: {
        customer: getCustomerDomain(),
        view: t('tanks:forms.add.analytics')
      },
      readOnly: false
    }
  }, [tank, dataForm, tankData, mainFormDefinition, carriersSelected, destinationsSelected])

  const getFabCustomActions = () => {
    let fabCustomActions: CustomAction[] = []

    if (defaultCosts.length > 0) {
      fabCustomActions.push({
        icon: <Add fontSize="small" />, //<Typography>Añadir</Typography>,
        tooltip: 'Añadir',
        onClick: () => onAddClickHandler(),
        userPermission: UserPermission.EDIT
      })

      fabCustomActions.push({
        icon: <AddCircleOutline fontSize="small" />, //<Typography>Añadir por defecto</Typography>,
        tooltip: 'Añadir costes definidos por transportista',
        onClick: () => onAddDefaultClickHandler(),
        userPermission: UserPermission.EDIT
      })
    }

    return fabCustomActions.length > 0 ? fabCustomActions : undefined
  }

  const carriersTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 3,
    download: true,
    canInsert: hasPermission(Resources.Tanks, [UserPermission.EDIT]) && !readOnly,
    canEdit: false,
    canDelete: false,
    selectableRows: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('tanks:forms.add.analytics')
    },
    customInsertEditModal: !readOnly,
    fabCustomActions: getFabCustomActions(),
    onRowInsert: async (row: any) => {
      if (defaultCosts.length == 0) {
        onAddClickHandler()
        return true
      } else {
        return false
      }
    },
    rowCustomActions: !readOnly
      ? [
          {
            userPermission: UserPermission.EDIT,
            icon: <Edit fontSize="small" />,
            tooltip: t('tanks:dataTablesAddAndEdit.actions.edit'),
            onClick: value => onEditClickHandler(value)
          },
          {
            userPermission: UserPermission.EDIT,
            icon: <Delete fontSize="small" />,
            tooltip: t('tanks:dataTablesAddAndEdit.actions.delete'),
            onClick: value => onDeleteClickHandler(value)
          }
        ]
      : undefined
  }

  const carriersTableColumns: DataTableColumn[] = [
    {
      name: 'carrierId',
      display: false
    },
    {
      name: 'description',
      label: t('tanks:forms.add.inputs.carrier'),
      hideForResponsive: false,
      type: DataType.TEXT
    },
    {
      name: 'quantity',
      label: t('tanks:forms.add.inputs.carrierQuantity'),
      type: DataType.NUMBER,
      decimalScale: DecimalScale.DEFAULT,
      display: !(
        hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) || hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
      )
    },
    {
      name: 'costType',
      label: t('tanks:forms.add.inputs.costType'),
      type: DataType.SELECT,
      selectOptions: dependencies.costTypes,
      display: !(
        hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) || hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
      )
    },
    {
      name: 'lgmaster',
      label: t('tanks:forms.add.inputs.lgMaster'),
      type: DataType.BOOLEAN,
      display: true
    }
  ]

  const destinationsTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 3,
    download: true,
    canDelete: false,
    canEdit: false,
    canInsert: hasPermission(Resources.Tanks, [UserPermission.EDIT]) && !readOnly,
    selectableRows: false,
    analytics: {
      customer: getCustomerDomain(),
      view: t('tanks:forms.add.analytics')
    },
    customInsertEditModal: !readOnly,
    fabDisabled:
      !dataForm.client || isFetchingDeps.originPlants || filteredDestinationsOptions.length === 0,
    fabDisabledTooltipLabel: !dataForm.client
      ? t('tanks:dataTablesAddAndEdit.destinationFABTooltipLabel')
      : filteredDestinationsOptions.length === 0
        ? t('tanks:dataTablesAddAndEdit.destinationFABTooltipLabelEmpty')
        : undefined,
    onRowInsert: async (row: any) => {
      onAddDestinationClickHandler()
      return true
    },
    rowCustomActions: !readOnly
      ? [
          {
            userPermission: UserPermission.EDIT,
            icon: <Edit fontSize="small" />,
            tooltip: t('tanks:dataTablesAddAndEdit.actions.edit'),
            onClick: value => onEditClickDestinationHandler(value)
          },
          {
            userPermission: UserPermission.EDIT,
            icon: <Delete fontSize="small" />,
            tooltip: t('tanks:dataTablesAddAndEdit.actions.delete'),
            onClick: value => onDeleteDestinationClickHandler(value)
          }
        ]
      : undefined
  }

  const destinationsTableColumns: DataTableColumn[] = React.useMemo<DataTableColumn[]>(() => {
    return [
      {
        name: 'destinationId',
        type: DataType.NUMBER,
        display: false
      },
      {
        name: 'description',
        label: t('tanks:forms.add.inputs.destination'),
        hideForResponsive: false,
        type: DataType.TEXT
      },
      {
        name: 'percentage',
        label: t('tanks:forms.add.inputs.percentage'),
        hideForResponsive: false,
        type: DataType.NUMBER,
        decimalScale: DecimalScale.ENERGY
      }
    ]
  }, [])

  const destinationsAddFormDef =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_DESTINATIONS',
        sections: [
          {
            inputs: [
              {
                name: 'destinationId',
                label: t('tanks:forms.add.inputs.description'),
                type: DataType.SELECT,
                selectOptions: filteredDestinationsOptions,
                required: true,
                isLoading: isFetchingDeps.destinations || isFetching,
                readOnly:
                  isFetchingDeps.destinations ||
                  isFetching ||
                  filteredDestinationsOptions.length === 1
              },
              {
                name: 'percentage',
                label: t('tanks:forms.add.inputs.percentage'),
                required: true,
                type: DataType.NUMBER,
                decimalScale: DecimalScale.ENERGY
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredDestinationsOptions, isFetchingDeps.destinations, isFetching])

  const destinationsEditFormDef =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_DESTINATIONS',
        sections: [
          {
            inputs: [
              {
                name: 'destinationId',
                label: t('tanks:forms.add.inputs.description'),
                type: DataType.SELECT,
                selectOptions: dependencies.destinations,
                required: true,
                readOnly: true,
                isLoading: isFetchingDeps.destinations || isFetching
              },
              {
                name: 'percentage',
                label: t('tanks:forms.add.inputs.percentage'),
                required: true,
                type: DataType.NUMBER,
                decimalScale: DecimalScale.ENERGY
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dependencies.destinations, isFetchingDeps.destinations, isFetching])

  const carriersAddFormDef =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_CARRIERS',
        sections: [
          {
            inputs: [
              {
                name: 'carrierId',
                label: t('tanks:forms.add.inputs.description'),
                type: DataType.SELECT,
                selectOptions: dependencies.carriers,
                required: true
              },
              {
                name: 'quantity',
                label: t('tanks:forms.add.inputs.carrierQuantity'),
                type: DataType.NUMBER,
                decimalScale: DecimalScale.DEFAULT,
                display: !(
                  hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
                  hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
                )
              },
              {
                name: 'costType',
                label: t('tanks:forms.add.inputs.costType'),
                type: DataType.SELECT,
                selectOptions: dependencies.costTypes,
                clearable: true,
                display: !(
                  hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) ||
                  hasRole(UserRoles.TANKS_REPSOL_NO_GESTIONADO)
                )
              },
              {
                name: 'lgmaster',
                label: t('tanks:forms.add.inputs.lgMaster'),
                type: DataType.BOOLEAN,
                required: true,
                defaultValue: carriersSelected.length > 0 ? false : true, // First carrier gets 'Main' property on
                readOnly: carriersSelected.length > 0 ? false : true // First carrier gets 'Main' property on
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dependencies.carriers, dependencies.costTypes, carriersSelected])

  const carriersAddDefaultFormDef =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'ADD_DEFAULT_COSTS',
        sections: [
          {
            inputs: [
              {
                name: 'carrierId',
                label: t('tanks:forms.addDefaultCosts.inputs.description'),
                type: DataType.SELECT,
                selectOptions: carriersDefaultCostsFormDeps,
                required: true
              }
            ]
          }
        ]
      }
    }, [carriersDefaultCostsFormDeps])

  const carriersEditFormDef =
    React.useMemo<FormDialogDefinitionProps>((): FormDialogDefinitionProps => {
      return {
        id: 'EDIT_CARRIERS',
        sections: [
          {
            inputs: [
              {
                name: 'carrierId',
                label: t('tanks:forms.add.inputs.description'),
                type: DataType.SELECT,
                selectOptions: dependencies.carriers,
                required: true,
                readOnly: true
              },
              {
                name: 'quantity',
                label: t('tanks:forms.add.inputs.carrierQuantity'),
                type: DataType.NUMBER,
                decimalScale: DecimalScale.DEFAULT
              },
              {
                name: 'costType',
                label: t('tanks:forms.add.inputs.costType'),
                type: DataType.SELECT,
                selectOptions: dependencies.costTypes,
                clearable: true
              },
              {
                name: 'lgmaster',
                label: t('tanks:forms.add.inputs.lgMaster'),
                type: DataType.BOOLEAN,
                required: true
              }
            ]
          }
        ]
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dependencies.carriers, dependencies.costTypes, carriersSelected])

  //#region form bottom

  const onAddClickHandler = () => {
    openForm({
      definition: carriersAddFormDef,
      title: t('tanks:forms.add.inputs.add'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddCarrierHandler
    })

    const initCarrier: Partial<CarrierTableModel> =
      dependencies.carriers.length === 1
        ? {
            carrierId: +dependencies.carriers[0].value,
            description: dependencies.carriers[0].label
          }
        : {}

    setCarriersFormDeps({
      carriers: dependencies.carriers,
      costTypes: dependencies.costTypes,
      formState: initCarrier
    })
  }

  const onAddDefaultClickHandler = () => {
    if (
      !dataForm.originPlant?.value ||
      !destinationsSelected ||
      destinationsSelected.length === 0
    ) {
      notifyError(t('tanks:dataTablesAddAndEdit.error.defaultCostsLackOfInfo'))
      return
    }

    var defaultCarriers = defaultCosts.filter(
      cost =>
        (!dataForm.originPlant?.value || cost.originPlantId == dataForm.originPlant.value) &&
        (!destinationsSelected ||
          destinationsSelected.length === 0 ||
          (cost.destiniesIds.every(destiny =>
            destinationsSelected.map(d => d.destinationId).includes(destiny)
          ) &&
            destinationsSelected.every(destiny =>
              cost.destiniesIds.map(d => d).includes(destiny.destinationId)
            )))
    )

    if (!defaultCarriers || defaultCarriers.length === 0) {
      notifyError(t('tanks:dataTablesAddAndEdit.error.defaultCostsNotFound2'))
      return
    }

    openForm({
      definition: carriersAddDefaultFormDef,
      title: t('tanks:forms.addDefaultCosts.inputs.add'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddDefaultCarrierHandler
    })

    const defaultCarriersOptions: SelectOption[] = dependencies.carriers.filter(carrier =>
      defaultCarriers.some(cost => cost.deliveryId === carrier.value)
    )

    setCarriersDefaultCostsFormDeps(defaultCarriersOptions)
  }

  const onEditClickHandler = async (item: CarrierTableModel) => {
    openForm({
      definition: carriersEditFormDef,
      title: t('tanks:forms.edit.titleCarrier'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: item => onEditCarrierHandler(item)
    })

    setCarriersFormDeps({
      carriers: dependencies.carriers,
      costTypes: dependencies.costTypes,
      formState: item
    })
  }

  const onAddCarrierHandler = async (item: CarrierTableModel) => {
    if (item) {
      const results = carriersSelected.filter(carrier => {
        return carrier.carrierId === item.carrierId && carrier.costType === item.costType
      })

      if (results.length > 0) {
        notifyError(t('tanks:dataTablesAddAndEdit.duplicateCarrier'))
      } else {
        const newCarrier = mapperOnSaveCarriersTable(item, dependencies.carriers)
        newCarrier.reactId = Math.random()

        setCarriersSelected(carriers => {
          // If new item is set to Master, all carriers on list must be set false.
          if (newCarrier.lgmaster) {
            const carriersWithoutMain = carriers.map(c => ({ ...c, lgmaster: false }))
            return [...carriersWithoutMain, newCarrier]
          }

          return [...carriers, newCarrier]
        })
      }
    }

    closeForm()
  }

  const onAddDefaultCarrierHandler = async (item: SelectOption) => {
    if (item) {
      var results = defaultCosts.filter(
        cost =>
          (!dataForm.originPlant?.value || cost.originPlantId == dataForm.originPlant.value) &&
          (!destinationsSelected ||
            destinationsSelected.length === 0 ||
            (cost.destiniesIds.every(destiny =>
              destinationsSelected.map(d => d.destinationId).includes(destiny)
            ) &&
              destinationsSelected.every(destiny =>
                cost.destiniesIds.map(d => d).includes(destiny.destinationId)
              ))) &&
          cost.deliveryId == item[0].value
      )

      if (!results || results.length === 0) {
        notifyError(t('tanks:dataTablesAddAndEdit.error.defaultCostsNotFound'))
      } else if (results.length > 2) {
        notifyError(t('tanks:dataTablesAddAndEdit.error.defaultCostsDuplicatedConfig'))
      } else {
        const newCarriers: CarrierTableModel[] = results[0].costs.map(cost => ({
          carrierId: Number(results[0].deliveryId),
          description: item[0].label,
          quantity: cost.quantity,
          costType: cost.costType,
          lgmaster: false,
          canDelete: !readOnly,
          reactId: Math.random()
        }))

        setCarriersSelected(carriers => {
          const hasMainCarrier = carriers.some(c => c.lgmaster === true)

          // If first item is set to Master, all carriers on list must be set false.
          if (!hasMainCarrier) {
            newCarriers[0].lgmaster = true
          }

          // Filter out carriers that match carrierId and costType with newCarriers
          const filteredCarriers = carriers.filter(
            c => !newCarriers.some(nc => nc.carrierId === c.carrierId && nc.costType === c.costType)
          )

          return [...filteredCarriers, ...newCarriers]
        })
      }
    }

    closeForm()
  }

  const onEditCarrierHandler = async (item: CarrierTableModel) => {
    if (item) {
      setCarriersSelected(carriers => {
        let updatedCarriers = carriers

        // If new item is set to Master, all carriers on list must be set false.
        if (item.lgmaster) {
          updatedCarriers = carriers.map(c => ({ ...c, lgmaster: false }))
        }

        if (item.reactId) {
          return updatedCarriers.map(carrier => {
            if (carrier.reactId === item.reactId) {
              return mapperOnSaveCarriersTable(item, dependencies.carriers)
            }
            return carrier
          })
        }

        return updatedCarriers
      })
    }
    closeForm()
  }

  const onDeleteClickHandler = async (item: CarrierTableModel) => {
    setCarriersSelected(
      carriersSelected
        .map((carrier, idx) => {
          if (idx === 0 && item.lgmaster) {
            return { ...carrier, lgmaster: true }
          }
          return carrier
        })
        // Como se permite tener transportistas duplicados (mismo Transportista con distinto costType), filtramos por ID y por costType para eliminar únicamente el clickeado
        .filter(x => !(x.carrierId === item.carrierId && x.costType === item.costType))
    )
  }

  // Destinations
  const onAddDestinationClickHandler = () => {
    openForm({
      definition: destinationsAddFormDef,
      title: t('tanks:forms.add.inputs.add'),
      mode: FormDialogMode.ADD,
      isWaiting: false,
      onClose: onAddDestinationHandler
    })

    var availableDestinations =
      dependencies.destinations.filter(
        option => !destinationsSelected.map(d => d.destinationId)?.includes(+option.value)
      ) ?? []

    const initDestination: Partial<DestinationTableModel> =
      availableDestinations.length === 1
        ? {
            destinationId: +availableDestinations[0].value,
            description: availableDestinations[0].label
          }
        : {}

    setDestinationsFormDeps(prevDependencies => ({
      ...prevDependencies,
      formState: initDestination
    }))
  }

  const onAddDestinationHandler = async (item: DestinationTableModel) => {
    if (item) {
      const newDestination = mapperOnSaveDestinationsTable(item, dependencies.destinations)
      var results = destinationsSelected.filter(des => {
        return des.destinationId === newDestination.destinationId
      })
      if (results.length > 0) {
        notifyError(t('tanks:dataTablesAddAndEdit.duplicateDestiny'))
      } else {
        setDestinationsSelected(destinations => {
          const updatedDestinationsList = [...destinations, newDestination]
          getOriginPlants(
            updatedDestinationsList.map(dest => dest.destinationId),
            dataForm.client?.value ? +dataForm.client.value : undefined
          )
          return updatedDestinationsList
        })
      }
    }
    closeForm()
  }

  const onEditDestinationHandler = async (item: DestinationTableModel) => {
    if (item) {
      let object = destinationsSelected.find(x => x.destinationId === item.destinationId)
      let index = destinationsSelected.indexOf(object!)
      destinationsSelected[index].percentage = item.percentage
      let dataFinal = [...destinationsSelected]

      // No need to re-fetch OriginPlants since Destination ID cannot be changed once Destination is created.
      setDestinationsSelected(dataFinal)
    }
    closeForm()
  }

  const onEditClickDestinationHandler = async (item: DestinationTableModel) => {
    openForm({
      definition: destinationsEditFormDef,
      title: t('tanks:forms.edit.titleDestination'),
      mode: FormDialogMode.EDIT,
      isWaiting: false,
      onClose: onEditDestinationHandler
    })

    setDestinationsFormDeps(prevDeps => ({
      ...prevDeps,
      formState: { destinationId: item.destinationId, percentage: item.percentage }
    }))
  }

  const onDeleteDestinationClickHandler = async (item: DestinationTableModel) => {
    const updatedList = destinationsSelected.filter(x => x.destinationId !== item.destinationId)
    // Origin Plants re-fetch for destinations on the list
    getOriginPlants(
      updatedList.map(e => e.destinationId),
      dataForm.client?.value ? +dataForm.client.value : undefined
    )

    //Bug 49704
    if (hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) && updatedList.length == 0) {
      setDataForm({
        ...dataForm!,
        originPlant: undefined
      })
    }

    setDestinationsSelected(updatedList)
  }

  const updateOriginAndDestinations = (clientID: number) => {
    // When Client is changed, OriginPlants and Destinations are no longer valid.
    setDataForm(state => ({ ...state, originPlant: undefined }))
    setDestinationsSelected([])

    // Fetch new origin plants and destinations for this client.
    getOriginPlants([], +clientID)

    // Fetch New Destinations will be triggered by useEffect.

    // Save selected Client
    setSelectedClient(+clientID)
  }

  const handleClientChange = async (clientID: number) => {
    if (!dataForm.originPlant && destinationsSelected.length === 0) {
      updateOriginAndDestinations(clientID)
    } else {
      const clientDescription = dependencies.clients.find(c => c.value === clientID)?.label
      await getConfirmation(
        ConfirmationDialogs.CHANGE_CLIENT,
        `${t('tanks:notifications.confirmationDialog.willLoadPlantsAndDestinations')} ${
          clientDescription ?? ''
        }. ${t('tanks:notifications.confirmationDialog.willLosePendingChanges')}`,
        true
      ).then(
        () => {
          updateOriginAndDestinations(clientID)
          closeConfirmation()
        },
        cancel => {
          setDataForm(data => ({
            ...data,
            client: dependencies.clients.find(c => c.value === selectedClient)
          }))
        }
      )
    }
  }

  const handleTankSwitchChange = throttle(async (switchValue: boolean) => {
    setSwitchFullTank(switchValue)

    if (switchValue) {
      let quantity = dependencies.optionsQuantity.find(
        c => c.label === DEFAULT_FULL_TANK_QUANTITY.toString()
      )
      setDataForm(data => ({
        ...data,
        quantity: quantity?.value,
        kg: calculateKgQuantity(dataForm.pcs, DEFAULT_FULL_TANK_QUANTITY),
        quantityOption: quantity,
        orderQuantity: DEFAULT_FULL_TANK_QUANTITY,
        orderKg: calculateKgQuantity(dataForm.pcs, DEFAULT_FULL_TANK_QUANTITY)
      }))
    } else {
      let quantity = dependencies.optionsQuantity.find(
        c => c.label === DEFAULT_NOT_FULL_TANK_QUANTITY.toString()
      )
      setDataForm(data => ({
        ...data,
        quantity: quantity?.value,
        kg: calculateKgQuantity(dataForm.pcs, DEFAULT_NOT_FULL_TANK_QUANTITY),
        quantityOption: quantity,
        orderQuantity: DEFAULT_NOT_FULL_TANK_QUANTITY,
        orderKg: calculateKgQuantity(dataForm.pcs, DEFAULT_NOT_FULL_TANK_QUANTITY)
      }))
    }
  }, 1000)

  const handleOriginPlantsChange = (plantID: number) => {
    getDestinations(
      destinationsSelected.length > 0 ? destinationsSelected.map(dest => dest.destinationId) : [],
      dataForm && dataForm.client ? +dataForm.client?.value : undefined,
      plantID
    )
  }

  const calculateKgQuantity = (pcs: number, quantity: number): number => {
    return parseFloat((quantity / pcs).toFixed(2))
  }

  const calculateKwhQuantity = (pcs: number, kg: number): number => {
    return parseInt((kg * pcs).toFixed(0))
  }

  //#endregion

  //#region useEffect
  useEffect(() => {
    fetchDependencies()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (destinationsSelected) {
      getDestinations(
        destinationsSelected.length > 0 ? destinationsSelected.map(dest => dest.destinationId) : [],
        dataForm && dataForm.client ? +dataForm.client?.value : undefined,
        dataForm && dataForm.originPlant?.value ? +dataForm.originPlant.value : undefined
      )
    }
  }, [destinationsSelected])

  useEffect(() => {
    updateFormDefinition(carriersAddFormDef, carriersFormDeps.formState)
  }, [carriersFormDeps.formState, carriersAddFormDef])

  useEffect(() => {
    updateFormDefinition(carriersAddDefaultFormDef, carriersDefaultCostsFormDeps)
  }, [carriersDefaultCostsFormDeps, carriersAddDefaultFormDef])

  useEffect(() => {
    updateFormDefinition(carriersEditFormDef, carriersFormDeps.formState)
  }, [carriersFormDeps.formState, carriersEditFormDef])

  useEffect(() => {
    updateFormDefinition(destinationsAddFormDef, destinationsFormDeps.formState)
  }, [destinationsFormDeps.formState, destinationsAddFormDef])

  useEffect(() => {
    updateFormDefinition(destinationsEditFormDef, destinationsFormDeps.formState)
  }, [destinationsFormDeps.formState, destinationsEditFormDef])

  //#endregion

  return (
    <Page>
      <PageHeader title={t(`tanks:forms.${tank ? 'edit' : 'add'}.title`)}>
        <div>
          <Grid container direction="row" justify="flex-end" spacing={2}>
            <Grid item>
              <Button
                size="small"
                variant="text"
                color="primary"
                // disabled={isFetching}
                onClick={() => {
                  history.goBack()
                }}
              >
                {t('tanks:forms.edit.inputs.cancel')}
              </Button>
            </Grid>
            <Grid item>
              <Button
                size="small"
                variant={!hasRole(UserRoles.TANKS_REPSOL_TPA) ? 'contained' : 'text'}
                color="primary"
                disabled={disableSave || isSaving}
                onClick={() => {
                  tank ? editTankShipment(dataForm, false) : createTankShipment(dataForm, false)
                }}
              >
                {t('tanks:forms.edit.inputs.save')}
              </Button>
            </Grid>
            {hasRole(UserRoles.TANKS_REPSOL_TPA) && (
              <Grid item>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  disabled={disableSave || isSaving}
                  onClick={() => {
                    tank ? editTankShipment(dataForm, true) : createTankShipment(dataForm, true)
                  }}
                >
                  {t('tanks:forms.edit.inputs.saveAndValidate')}
                </Button>
              </Grid>
            )}
          </Grid>
        </div>
      </PageHeader>
      <Card>
        <>
          {!isFetching && !isFetchingTank && !isFetchingOriginPlants ? (
            <Form title={''} sections={mainFormDefinition} data={dataForm} options={options} />
          ) : (
            <LoadingForm />
          )}
          <Box m={2}>
            <Grid container spacing={2} justify="space-between">
              <Grid item xs={12} sm={12} md={5}>
                <Typography variant="subtitle1" color="primary" className={classes.tableTitles}>
                  {t('tanks:dataTablesAddAndEdit.destinationsTitle')}
                </Typography>
                {!isFetching && !isFetchingDestinations ? (
                  <DataTable
                    data={destinationsSelected}
                    columns={destinationsTableColumns}
                    userPermissions={[UserPermission.EDIT]}
                    options={destinationsTableOptions}
                  />
                ) : (
                  <DataTableSkeleton rows={2} />
                )}
              </Grid>
              {!hasRole(UserRoles.TANKS_REPSOL_GESTIONADO) && (
                <Grid item xs={12} sm={12} md={5}>
                  <Typography variant="subtitle1" color="primary" className={classes.tableTitles}>
                    {t('tanks:dataTablesAddAndEdit.carriersTable')}
                  </Typography>
                  {!isFetching && !isFetchingTank && !isFetchingOriginPlants ? (
                    <DataTable
                      data={carriersSelected}
                      columns={carriersTableColumns}
                      userPermissions={[UserPermission.EDIT]}
                      options={carriersTableOptions}
                    />
                  ) : (
                    <DataTableSkeleton rows={2} />
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        </>
      </Card>
      <CustomFormDialog {...formProps}></CustomFormDialog>
      <ErrorDetailList {...errorDetailListDialogProps} />
    </Page>
  )
}
